import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Menu } from '@headlessui/react';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  EyeIcon,
  DocumentArrowDownIcon,
  DocumentTextIcon,
  ChevronDownIcon,
  ShoppingCartIcon,
  DocumentDuplicateIcon,
} from '@heroicons/react/24/outline';
import { quotationService, Quotation, ConvertQuotationResponse } from '../../services/quotationService';
import { QuotationModal } from '../../components/quotations/QuotationModal';
import toast from 'react-hot-toast';

const statusColors: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-800',
  sent: 'bg-blue-100 text-blue-800',
  accepted: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
  converted: 'bg-purple-100 text-purple-800',
};

const statusLabels: Record<string, string> = {
  draft: 'Draft',
  sent: 'Sent',
  accepted: 'Accepted',
  rejected: 'Rejected',
  converted: 'Converted',
};

export default function QuotationsListPage() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [selectedQuotation, setSelectedQuotation] = useState<Quotation | null>(null);
  const [showQuotationModal, setShowQuotationModal] = useState(false);
  const [previewDocType, setPreviewDocType] = useState<'quotation' | 'proforma'>('quotation');

  const { data: quotationsData } = useQuery({
    queryKey: ['quotations', page, statusFilter],
    queryFn: () => quotationService.getAll({ page, limit: 20, status: statusFilter || undefined }),
  });

  const quotations = quotationsData?.data?.data?.items || [];
  const pagination = quotationsData?.data?.data?.pagination;

  const filteredQuotations = quotations.filter((q: Quotation) => {
    const search = searchQuery.toLowerCase();
    const customerName = q.customers
      ? q.customers.company_name || `${q.customers.first_name} ${q.customers.last_name}`
      : q.customer_name || 'Walk-in Customer';
    return (
      q.quotation_number.toLowerCase().includes(search) ||
      customerName.toLowerCase().includes(search)
    );
  });

  const handleView = (quotation: Quotation) => {
    setSelectedQuotation(quotation);
    setPreviewDocType('quotation');
    setShowQuotationModal(true);
  };

  const handlePreviewProforma = (quotation: Quotation) => {
    setSelectedQuotation(quotation);
    setPreviewDocType('proforma');
    setShowQuotationModal(true);
  };

  const handleConvertToSale = async (quotation: Quotation) => {
    try {
      const response = await quotationService.convertToSale(quotation.quotation_id);
      const convertData = (response.data as any).data as ConvertQuotationResponse;
      // Store quotation data in sessionStorage for POS page
      sessionStorage.setItem('quotationToConvert', JSON.stringify(convertData));
      navigate('/pos', { state: { quotationData: convertData } });
    } catch {
      toast.error('Failed to convert quotation');
    }
  };

  const getCustomerName = (quotation: Quotation) => {
    if (quotation.customers) {
      return quotation.customers.company_name || `${quotation.customers.first_name || ''} ${quotation.customers.last_name || ''}`.trim();
    }
    return quotation.customer_name || 'Walk-in Customer';
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-gray-900">Quotations</h1>
        <div className="flex items-center gap-3">
          {/* <button
            onClick={() => navigate('/quotations/company-items')}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
          >
            <DocumentTextIcon className="w-5 h-5" />
            Company Items
          </button> */}
          <button
            onClick={() => navigate('/quotations/new')}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            <PlusIcon className="w-5 h-5" />
            New Quotation
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
        <div className="flex gap-4">
          <div className="relative" style={{ flex: '4' }}>
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search quotations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            style={{ flex: '1' }}
          >
            <option value="">All Status</option>
            <option value="draft">Draft</option>
            <option value="sent">Sent</option>
            <option value="accepted">Accepted</option>
            <option value="rejected">Rejected</option>
            <option value="converted">Converted</option>
          </select>
        </div>
      </div>

      {/* Quotations Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Quotation #
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Customer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Expiry
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredQuotations.map((quotation: Quotation) => (
                <tr key={quotation.quotation_id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {quotation.quotation_number}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{getCustomerName(quotation)}</div>
                    {quotation.customers?.email && (
                      <div className="text-sm text-gray-500">{quotation.customers.email}</div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {new Date(quotation.quotation_date).toLocaleDateString()}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {quotation.expiry_date
                        ? new Date(quotation.expiry_date).toLocaleDateString()
                        : '-'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      KSh {Number(quotation.total_amount).toLocaleString()}
                    </div>
                    <div className="text-xs text-gray-500">
                      {quotation.quotation_items.length} item(s)
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        statusColors[quotation.status]
                      }`}
                    >
                      {statusLabels[quotation.status]}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => handleView(quotation)}
                        className="p-1 text-gray-400 hover:text-primary-600"
                        title="View"
                      >
                        <EyeIcon className="w-5 h-5" />
                      </button>
                      {quotation.status !== 'converted' && (
                        <Menu as="div" className="relative">
                          <Menu.Button
                            className="flex items-center gap-1 p-1 text-gray-400 hover:text-green-600"
                            title="Convert"
                          >
                            <DocumentArrowDownIcon className="w-5 h-5" />
                            <ChevronDownIcon className="w-3.5 h-3.5" />
                          </Menu.Button>
                          <Menu.Items className="absolute right-0 z-10 mt-1 w-56 origin-top-right rounded-lg bg-white shadow-lg ring-1 ring-black/5 focus:outline-none">
                            <div className="py-1">
                              <Menu.Item>
                                {({ active }) => (
                                  <button
                                    onClick={() => handleConvertToSale(quotation)}
                                    className={`flex items-center gap-2 w-full px-4 py-2 text-sm text-left ${
                                      active ? 'bg-gray-50 text-gray-900' : 'text-gray-700'
                                    }`}
                                  >
                                    <ShoppingCartIcon className="w-4 h-4 text-gray-400" />
                                    Convert to Sale
                                  </button>
                                )}
                              </Menu.Item>
                              <Menu.Item>
                                {({ active }) => (
                                  <button
                                    onClick={() => handlePreviewProforma(quotation)}
                                    className={`flex items-center gap-2 w-full px-4 py-2 text-sm text-left ${
                                      active ? 'bg-gray-50 text-gray-900' : 'text-gray-700'
                                    }`}
                                  >
                                    <DocumentDuplicateIcon className="w-4 h-4 text-gray-400" />
                                    Convert to Pro-Forma Invoice
                                  </button>
                                )}
                              </Menu.Item>
                            </div>
                          </Menu.Items>
                        </Menu>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {filteredQuotations.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center text-gray-500">
                    <DocumentTextIcon className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                    <p>No quotations found</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination && pagination.pages > 1 && (
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
            <div className="text-sm text-gray-700">
              Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
              {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
              {pagination.total} quotations
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={pagination.page === 1}
                className="px-3 py-1 border border-gray-300 rounded-lg disabled:opacity-50 hover:bg-white"
              >
                Previous
              </button>
              <button
                onClick={() => setPage(p => Math.min(pagination.pages, p + 1))}
                disabled={pagination.page === pagination.pages}
                className="px-3 py-1 border border-gray-300 rounded-lg disabled:opacity-50 hover:bg-white"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Quotation Modal */}
      {showQuotationModal && selectedQuotation && (
        <QuotationModal
          isOpen={showQuotationModal}
          onClose={() => {
            setShowQuotationModal(false);
            setSelectedQuotation(null);
          }}
          quotation={selectedQuotation}
          documentType={previewDocType}
          onConvert={() => handleConvertToSale(selectedQuotation)}
        />
      )}
    </div>
  );
}
